import Excel from 'exceljs';
import isValidDomain from 'is-valid-domain';
import _ from 'lodash';

export const ALLOWED_FILE_EXTENSIONS = ['csv', 'xls', 'xlsx'];

// MIME types give proper OS file-picker labels (e.g. "CSV", "Excel") instead of "Custom Files".
// Extensions are included as a fallback when the browser reports an empty MIME type.
export const ALLOWED_FILE_ACCEPT = [
    'text/csv',
    'application/csv',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    '.csv',
    '.xls',
    '.xlsx',
].join(',');

export const ALLOWED_FILE_FORMATS = ALLOWED_FILE_ACCEPT;

export const PARSE_ERROR_TYPES = {
    COULD_NOT_PARSE: 'COULD_NOT_PARSE',
    NO_DOMAINS: 'NO_DOMAINS',
};

const getFileExtension = (fileName) => {
    const parts = fileName.split('.');
    return parts.length > 1 ? _.toLower(parts[parts.length - 1]) : '';
};

const readFileAsText = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onabort = () => reject(new Error('aborted'));
    reader.onerror = () => reject(new Error('failed'));
    reader.onload = () => resolve(reader.result);
    reader.readAsText(file);
});

const readFileAsArrayBuffer = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onabort = () => reject(new Error('aborted'));
    reader.onerror = () => reject(new Error('failed'));
    reader.onload = () => resolve(reader.result);
    reader.readAsArrayBuffer(file);
});

const normalizeCellValue = (value) => {
    if (_.isNil(value)) {
        return '';
    }

    if (typeof value === 'object' && value.text) {
        return String(value.text).trim();
    }

    return String(value).trim();
};

const dedupeRawValues = (rawValues) => {
    const values = [];
    const seen = new Set();

    for (const rawValue of rawValues) {
        const value = normalizeCellValue(rawValue);

        if (!_.isEmpty(value) && !seen.has(value)) {
            seen.add(value);
            values.push(value);
        }
    }

    return values;
};

const skipHeaderRow = (values) => {
    if (values.length > 0 && _.toLower(values[0]) === 'domain') {
        return values.slice(1);
    }

    return values;
};

const parseCsvValues = async (file) => {
    const content = await readFileAsText(file);
    return dedupeRawValues(content.split(/\r\n|\n|,/g));
};

const parseExcelValues = async (file) => {
    const buffer = await readFileAsArrayBuffer(file);
    const workbook = new Excel.Workbook();
    const values = [];

    await workbook.xlsx.load(buffer);

    const worksheet = workbook.getWorksheet(1);

    if (!worksheet) {
        throw new Error(PARSE_ERROR_TYPES.COULD_NOT_PARSE);
    }

    worksheet.getColumn(1).eachCell((cell) => {
        values.push(cell.value);
    });

    return dedupeRawValues(values);
};

export const parseDomainsFromFile = async (file) => {
    const fileExtension = getFileExtension(file.name);

    let rawValues = [];

    try {
        if (fileExtension === 'csv') {
            rawValues = await parseCsvValues(file);
        } else if (ALLOWED_FILE_EXTENSIONS.includes(fileExtension)) {
            rawValues = await parseExcelValues(file);
        } else {
            throw new Error(PARSE_ERROR_TYPES.COULD_NOT_PARSE);
        }
    } catch (error) {
        if (error.message === 'aborted' || error.message === 'failed') {
            throw error;
        }

        throw new Error(PARSE_ERROR_TYPES.COULD_NOT_PARSE);
    }

    rawValues = skipHeaderRow(rawValues);

    if (rawValues.length === 0) {
        throw new Error(PARSE_ERROR_TYPES.NO_DOMAINS);
    }

    return rawValues;
};

export const validateParsedDomains = (rawValues) => {
    const validDomains = [];
    const invalidDomains = [];

    for (const value of rawValues) {
        const domain = {label: value, value, __isNew__: true};

        if (!isValidDomain(value)) {
            invalidDomains.push(domain);
        } else {
            domain.value = _.toLower(value);
            domain.label = domain.value;
            validDomains.push(domain);
        }
    }

    return {validDomains, invalidDomains};
};
